/**
 * Pantalla de la consola, contra el backend de Rubén.
 *
 * Desde BB-2004 son tres pestañas: **Elegibles** (el universo pagable de siempre), **Soft killers**
 * (viajes que Data rechazó y que ya están en estado de pago bloqueado; solo revisión hoy) y
 * **Registro** (el audit log, común a los dos programas). Las dos primeras son la MISMA pantalla
 * (`PanelPrograma`) con distinta configuración (`programas.ts`) y distinta callable de preview; la
 * separación en pestañas es la decisión de producto: Ops no debe mezclar nunca el universo de soft
 * killers con el pagable al decidir qué confirma.
 *
 * El registro va en su propia pestaña y no debajo de cada programa porque las dos confirmaciones
 * escriben en el mismo `audit_log`: no pertenece a ninguna de las dos. Al confirmar, la app salta a
 * esa pestaña, enseña el aviso allí y recarga el registro, donde la fila nueva sale destacada.
 *
 * Lo que aquí queda es lo global: login, el modo del backend (banner de demo), el ritmo del endpoint
 * de pago (que el registro necesita para sus estimaciones) y la pestaña activa, que se refleja en el
 * hash de la URL para que recargar no te devuelva a la primera.
 */
import { Alert, Button, Container, Group, Loader, Stack, Tabs, Text, Title } from '@mantine/core';
import type { BackendMode, PreviewResponse } from '@caes-payments/shared';
import { GoogleAuthProvider, onAuthStateChanged, signInWithPopup, signOut, type User } from 'firebase/auth';
import { useCallback, useEffect, useState } from 'react';

import { AuditLog } from './components/AuditLog';
import { PanelPrograma } from './components/PanelPrograma';
import { AVISO_MODO_MOCK, type AvisoConfirmacion } from './confirmacion';
import { auth, llamarConfirm, llamarFicheros, llamarPreview, llamarPreviewSoftKiller } from './firebase';
import { MARCA_CONFIRMACION_ELEGIBLES, PROGRAMA_ELEGIBLES, PROGRAMA_SOFT_KILLER } from './programas';

/** Valores de las pestañas. Van al hash de la URL, así que son estables y legibles. */
export const PESTANAS = {
  elegibles: 'elegibles',
  softKiller: 'soft-killers',
  registro: 'registro',
} as const;
type Pestana = (typeof PESTANAS)[keyof typeof PESTANAS];

function pestanaDelHash(): Pestana {
  const hash = window.location.hash.replace(/^#/, '');
  return (Object.values(PESTANAS) as string[]).includes(hash) ? (hash as Pestana) : PESTANAS.elegibles;
}

// Las callables devuelven `{ data }`; el panel quiere la respuesta a secas. Se desenvuelven aquí,
// una vez, y así la vista de diseño puede inyectar funciones normales.
const previsualizarElegibles = (c: Parameters<typeof llamarPreview>[0]) =>
  llamarPreview(c).then((r) => r.data);
const previsualizarSoftKiller = (c: Parameters<typeof llamarPreviewSoftKiller>[0]) =>
  llamarPreviewSoftKiller(c).then((r) => r.data);
const confirmar = (req: Parameters<typeof llamarConfirm>[0]) => llamarConfirm(req).then((r) => r.data);
// Definida a nivel de módulo, no inline en el JSX: el panel la usa como dependencia de un efecto, y
// una función nueva en cada render volvería a consultar los ficheros en cada repintado.
const cargarFicheros = () => llamarFicheros({}).then((r) => r.data);

async function refrescarToken() {
  await auth.currentUser?.getIdToken(true);
}

export function App() {
  const [usuario, setUsuario] = useState<User | null>(null);
  const [cargandoAuth, setCargandoAuth] = useState(true);
  const [errorLogin, setErrorLogin] = useState<string | null>(null);

  const [pestana, setPestana] = useState<Pestana>(pestanaDelHash);
  useEffect(() => {
    // replaceState y no `location.hash =`: cambiar de pestaña no es navegar, y no debe llenar el
    // historial de entradas que el botón "atrás" tendría que deshacer una a una.
    window.history.replaceState(null, '', `#${pestana}`);
  }, [pestana]);

  /**
   * Ritmo del endpoint de pago (`limiteRpsPago`, BB-1651). Es configuración del backend, no un dato
   * de un preview concreto, y el registro lo necesita para estimar lo que queda de un trabajo en
   * curso. `undefined` hasta el primer preview de la sesión — y entonces el contador dice
   * "calculando" en vez de inventarse un ritmo, que es la degradación honesta.
   */
  const [limiteRpsPago, setLimiteRpsPago] = useState<number | undefined>(undefined);
  const [modo, setModo] = useState<BackendMode | null>(null);
  const [confirmado, setConfirmado] = useState<AvisoConfirmacion | null>(null);
  // Cambiar este número recarga <AuditLog>. Desde BB-1617 el resultado de una confirmación solo
  // existe en el registro, así que tras confirmar hay que releerlo.
  const [recargasRegistro, setRecargasRegistro] = useState(0);

  useEffect(() => {
    return onAuthStateChanged(auth, async (u) => {
      // Refresco forzado del ID token al entrar. El claim `caes_console` viaja DENTRO del token, y un
      // token emitido antes del alta no lo lleva: sin esto, a quien acaban de dar de alta le sigue
      // diciendo que no está en la lista hasta que el token caduca por su cuenta (hasta 1h).
      if (u) {
        try {
          await u.getIdToken(true);
        } catch {
          // Si el refresco falla se sigue: la llamada dará el error real, más informativo.
        }
      }
      setUsuario(u);
      setCargandoAuth(false);
    });
  }, []);

  const alLlegarPreview = useCallback((p: PreviewResponse) => {
    setModo(p.mode);
    setLimiteRpsPago(p.limiteRpsPago);
  }, []);

  const alConfirmar = useCallback((aviso: AvisoConfirmacion) => {
    setConfirmado(aviso);
    // Se recarga el registro y se salta a él: es donde vive el resultado ahora, y dejar a Ops en la
    // pestaña del programa sería mandarle a buscar lo que acaba de hacer.
    setRecargasRegistro((n) => n + 1);
    setPestana(PESTANAS.registro);
  }, []);

  if (cargandoAuth) return <Loader m="xl" />;

  if (!usuario) {
    return (
      <Container size="xs" py="xl">
        <Stack>
          <Title order={2}>CAEs Payments Console</Title>
          <Button
            onClick={() =>
              signInWithPopup(auth, new GoogleAuthProvider()).catch((err: unknown) =>
                setErrorLogin(err instanceof Error ? err.message : String(err)),
              )
            }
          >
            Entrar con Google
          </Button>
          {errorLogin && <Alert color="red">{errorLogin}</Alert>}
        </Stack>
      </Container>
    );
  }

  return (
    <>
      {/* Atado a BACKEND_MODE: desaparece por sí solo el día que pase a 'real'. No es una etiqueta de
          debug, es lo único que impide que alguien lea las cifras como reales. */}
      {modo === 'mock' && <div className="aviso-demo">{AVISO_MODO_MOCK}</div>}

      {/* `data-confirmacion`: la marca que lee verify-deployment.sh en el bundle servido. Ver
          MARCA_CONFIRMACION_ELEGIBLES en programas.ts. */}
      <Container size="lg" py="lg" data-confirmacion={MARCA_CONFIRMACION_ELEGIBLES}>
        <Group justify="space-between" mb="md">
          <Title order={1} fz="h3">
            CAEs Payments Console
          </Title>
          <Group gap="xs">
            <Text size="xs" c="dimmed">
              {usuario.email}
            </Text>
            <Button variant="subtle" size="xs" onClick={() => signOut(auth)}>
              Salir
            </Button>
          </Group>
        </Group>

        {/* keepMounted (el default de Mantine) es lo que hace que cambiar de pestaña no pierda el
            preview ni los filtros de la otra: cada panel guarda su estado mientras la app viva. */}
        <Tabs value={pestana} onChange={(v) => setPestana((v as Pestana) ?? PESTANAS.elegibles)}>
          <Tabs.List mb="lg">
            <Tabs.Tab value={PESTANAS.elegibles}>{PROGRAMA_ELEGIBLES.pestana}</Tabs.Tab>
            <Tabs.Tab value={PESTANAS.softKiller}>{PROGRAMA_SOFT_KILLER.pestana}</Tabs.Tab>
            <Tabs.Tab value={PESTANAS.registro}>Registro</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value={PESTANAS.elegibles}>
            <PanelPrograma
              programa={PROGRAMA_ELEGIBLES}
              previsualizar={previsualizarElegibles}
              confirmar={confirmar}
              onPreview={alLlegarPreview}
              onConfirmado={alConfirmar}
              refrescarToken={refrescarToken}
              cargarFicheros={cargarFicheros}
            />
          </Tabs.Panel>

          <Tabs.Panel value={PESTANAS.softKiller}>
            <PanelPrograma
              programa={PROGRAMA_SOFT_KILLER}
              previsualizar={previsualizarSoftKiller}
              confirmar={confirmar}
              onPreview={alLlegarPreview}
              onConfirmado={alConfirmar}
              refrescarToken={refrescarToken}
            />
          </Tabs.Panel>

          <Tabs.Panel value={PESTANAS.registro}>
            <Stack gap="lg">
              {/* El titulo sale de mensajeDeConfirmacion, no escrito aqui: cuando /confirm paso a
                  encolar, un title="Confirmado" fijo se quedo contradiciendo al cuerpo. */}
              {confirmado && (
                <Alert
                  color="green"
                  title={confirmado.titulo}
                  withCloseButton
                  onClose={() => setConfirmado(null)}
                >
                  {confirmado.cuerpo}
                </Alert>
              )}
              {/* `recargarToken`, no `key`: con `key` el registro se remontaba en cada confirmación y
                  perdía la lista anterior, así que no podía saber qué fila es nueva. */}
              {/* `uidActual` decide en qué filas se ofrece la X de cancelar (BB-1676): solo en las de
                  quien está mirando. Es UX — el permiso lo comprueba el backend. */}
              <AuditLog
                recargarToken={recargasRegistro}
                limiteRpsPago={limiteRpsPago}
                uidActual={usuario.uid}
              />
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </Container>
    </>
  );
}
